import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { ResourceLink } from '../../types';
import {
  Bookmark,
  ExternalLink,
  Plus,
  Search,
  Pin,
  Copy,
  Check,
  Edit3,
  Trash2,
  Globe,
  X,
  Link as LinkIcon,
} from 'lucide-react';

export const ResourcesView: React.FC = () => {
  const {
    resources,
    addResource,
    editResource,
    deleteResource,
    togglePinResource,
    triggerConfetti,
  } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [quickTitle, setQuickTitle] = useState('');
  const [quickUrl, setQuickUrl] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<ResourceLink | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editPinned, setEditPinned] = useState(false);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditModalOpen) {
        setIsEditModalOpen(false);
        setEditingResource(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditModalOpen]);

  // Clean URL helper
  const normalizeUrl = (url: string) => {
    let clean = url.trim();
    if (!clean) return '';
    if (!/^https?:\/\//i.test(clean)) {
      clean = 'https://' + clean;
    }
    return clean;
  };

  // Quick Add (Press Enter to Save)
  const handleQuickAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickTitle.trim() && !quickUrl.trim()) return;

    let title = quickTitle.trim();
    const url = normalizeUrl(quickUrl);

    // If user only provided a URL, extract title from domain
    if (!title && url) {
      try {
        const domain = new URL(url).hostname.replace('www.', '');
        title = domain.charAt(0).toUpperCase() + domain.slice(1);
      } catch {
        title = url;
      }
    }

    addResource({
      title: title || 'Saved Link',
      url: url || 'https://google.com',
      category: 'other',
      tags: [],
      isPinned: false,
    });

    triggerConfetti();
    setQuickTitle('');
    setQuickUrl('');
  };

  // Open Edit Modal
  const handleOpenEdit = (res: ResourceLink) => {
    setEditingResource(res);
    setEditTitle(res.title);
    setEditUrl(res.url);
    setEditPinned(Boolean(res.isPinned));
    setIsEditModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingResource || !editTitle.trim()) return;

    editResource(editingResource.id, {
      title: editTitle.trim(),
      url: normalizeUrl(editUrl) || editingResource.url,
      isPinned: editPinned,
    });

    setIsEditModalOpen(false);
    setEditingResource(null);
  };

  // Copy Link
  const handleCopyLink = (id: string, url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Filter & Sort Resources (Pinned first, then newest)
  const filteredResources = useMemo(() => {
    const list = (resources || []).filter((r) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = r.title.toLowerCase().includes(q);
        const matchUrl = r.url.toLowerCase().includes(q);
        return matchTitle || matchUrl;
      }
      return true;
    });

    // Pinned first, then sorted by createdAt descending
    return list.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
  }, [resources, searchQuery]);

  return (
    <div className="space-y-6 animate-fade-in pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center">
            <Bookmark className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>Resources & Links</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 font-bold">
                {(resources || []).length} saved
              </span>
            </h1>
          </div>
        </div>
      </div>

      {/* Simple Quick Add Form: Title + Link -> Hit Enter */}
      <form
        onSubmit={handleQuickAdd}
        className="p-3 bg-white dark:bg-[#111827] border-2 border-cyan-500/30 hover:border-cyan-500/80 rounded-3xl shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 transition-all"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 pl-2">
          <LinkIcon className="w-4 h-4 text-cyan-500 flex-shrink-0" />
          <input
            type="text"
            placeholder="Title (e.g. GitHub, OpenAI Docs, Supabase...)"
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            className="w-full py-1.5 bg-transparent text-xs sm:text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
            autoFocus
          />
        </div>

        <div className="h-px sm:h-6 w-full sm:w-px bg-slate-200 dark:bg-slate-800" />

        <div className="flex items-center gap-2 flex-1 min-w-0 pl-2">
          <Globe className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Link / URL (e.g. https://...)"
            value={quickUrl}
            onChange={(e) => setQuickUrl(e.target.value)}
            required
            className="w-full py-1.5 bg-transparent text-xs sm:text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
          />
        </div>

        <button
          type="submit"
          disabled={!quickUrl.trim() && !quickTitle.trim()}
          className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 flex-shrink-0 ${
            quickUrl.trim() || quickTitle.trim()
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-md shadow-cyan-500/25 active:scale-95'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
          }`}
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Save Link</span>
        </button>
      </form>

      {/* Search Bar */}
      <div className="p-3 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-xs">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search saved resources & links..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Resources Cards Grid */}
      {filteredResources.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 text-cyan-500 flex items-center justify-center mx-auto">
            <Bookmark className="w-7 h-7" />
          </div>
          <h3 className="text-base font-black text-slate-900 dark:text-white">
            {searchQuery ? 'No matching resources found' : 'No links saved yet'}
          </h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {searchQuery
              ? 'Try searching with different keywords.'
              : 'Enter a title and link above and press Enter to save your first bookmark!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredResources.map((res) => {
            const domain = res.url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
            const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
            const isCopied = copiedId === res.id;

            return (
              <div
                key={res.id}
                className={`bg-white dark:bg-[#111827] border rounded-3xl p-5 shadow-xs flex flex-col justify-between min-h-[140px] transition-all hover:shadow-lg hover:-translate-y-0.5 group ${
                  res.isPinned
                    ? 'border-cyan-500/40 bg-cyan-500/[0.02]'
                    : 'border-slate-200/80 dark:border-slate-800 hover:border-cyan-500/50'
                }`}
              >
                <div>
                  {/* Top Bar: Icon, Favicon & Action Buttons */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center p-1.5 flex-shrink-0 shadow-xs">
                        <img
                          src={faviconUrl}
                          alt={res.title}
                          className="w-4 h-4 object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLElement).style.display = 'none';
                          }}
                        />
                        <Globe className="w-4 h-4 text-slate-400" />
                      </div>

                      {res.isPinned && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 flex items-center gap-1">
                          <Pin className="w-2.5 h-2.5 fill-cyan-500" />
                          Pinned
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      {/* Pin Button */}
                      <button
                        onClick={() => togglePinResource(res.id)}
                        className={`p-1.5 rounded-xl transition-colors ${
                          res.isPinned
                            ? 'text-cyan-600 dark:text-cyan-400 bg-cyan-500/10'
                            : 'text-slate-400 hover:text-cyan-600 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                        title={res.isPinned ? 'Unpin Link' : 'Pin Link'}
                      >
                        <Pin className={`w-3.5 h-3.5 ${res.isPinned ? 'fill-cyan-500' : ''}`} />
                      </button>

                      {/* Edit Button */}
                      <button
                        onClick={() => handleOpenEdit(res)}
                        className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => deleteResource(res.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <a
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors line-clamp-2 block leading-snug"
                  >
                    {res.title}
                  </a>

                  {/* Domain */}
                  <p className="text-[11px] font-mono text-slate-400 mt-1 truncate">
                    {domain}
                  </p>
                </div>

                {/* Card Footer: Copy Link and Open Link */}
                <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 mt-3 flex items-center justify-between gap-2">
                  <button
                    onClick={(e) => handleCopyLink(res.id, res.url, e)}
                    className="px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                    title="Copy Link"
                  >
                    {isCopied ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-500" />
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>

                  <a
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 hover:bg-cyan-100 text-cyan-600 dark:text-cyan-400 text-xs font-bold flex items-center gap-1 transition-colors"
                  >
                    <span>Open</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Resource Modal */}
      {isEditModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsEditModalOpen(false);
              setEditingResource(null);
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/10 text-cyan-500 flex items-center justify-center">
                  <Bookmark className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Edit Resource Link</h3>
              </div>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingResource(null);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  Link / URL
                </label>
                <input
                  type="text"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs sm:text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Pin Toggle */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEditPinned(!editPinned)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${
                    editPinned
                      ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-transparent hover:text-cyan-500'
                  }`}
                >
                  <Pin className={`w-3.5 h-3.5 ${editPinned ? 'fill-cyan-500' : ''}`} />
                  <span>{editPinned ? 'Pinned to Top' : 'Pin to Top'}</span>
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingResource(null);
                  }}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-cyan-500/25 flex items-center gap-1.5 transition-all transform active:scale-95"
                >
                  <Check className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
